import { useNavigate } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import { AiOutlineArrowLeft } from "solid-icons/ai";
import { createMemo, createSignal, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { CompanyLayoutMainContentContainer } from "@/components/layout/CompanyLayout";
import { Container } from "@/components/shared/Container";
import { Button } from "@/components/ui/button";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { createGetAllAtsJobOffersQueryKey } from "@/domains/company/active-search/api";
import { SearchOfferCard } from "@/domains/company/active-search/components/SearchOfferCard";
import {
  useFreelanceSearchActions,
  useFreelanceSearchStore,
} from "@/domains/company/active-search/store/freelance-search-store";
import {
  buildPathWithQueryParams,
  freelancerFilterFormToQueryParams,
} from "@/domains/company/active-search/utils/filterParams";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { clearResultReachedViaRerun } from "@/domains/company/recent-searches/store/rerun-intent";

const ITEMS_PER_PAGE = 10;

export default function CompanySearchFreelancerOffersRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [companyStore] = useCompanyStore();
  const [freelanceSearchStore] = useFreelanceSearchStore();
  const { setJobOfferId } = useFreelanceSearchActions();
  const [visibleCount, setVisibleCount] = createSignal(ITEMS_PER_PAGE);

  const jobOffers = useQuery(() =>
    createGetAllAtsJobOffersQueryKey(companyStore.selectedCompany?.id ?? ""),
  );

  const visibleJobOffers = createMemo(() => {
    const allOffers = jobOffers.data?.job_offers ?? [];
    return allOffers.slice(0, visibleCount());
  });

  const hasMore = createMemo(() => {
    const allOffers = jobOffers.data?.job_offers ?? [];
    return visibleCount() < allOffers.length;
  });

  const handleJobOfferClick = (jobOfferId: string) => {
    // A normal offer-driven entry into `/result`, not a rerun: an earlier
    // rerun's flag must not leak into this fresh navigation (see rerun-intent.ts).
    clearResultReachedViaRerun();
    setJobOfferId(jobOfferId);

    const jobOffer = jobOffers.data?.job_offers.find(
      (offer) => offer.id === jobOfferId,
    );

    if (jobOffer) {
      const queryParams = freelancerFilterFormToQueryParams({
        job_offer_id: jobOfferId,
        search: jobOffer.title,
        skills: jobOffer.skills,
        job_category: jobOffer.job_category,
        job_subcategory: jobOffer.job_subcategory ?? [],
        work_preference: jobOffer.remote_policy,
        price_per_day_min: jobOffer.daily_rate_min?.toString(),
        price_per_day_max: jobOffer.daily_rate_max?.toString(),
        experience_level: jobOffer.experience_level,
      });

      navigate(
        buildPathWithQueryParams(
          "/profile/company/search/freelancers/result",
          queryParams,
        ),
      );
    }
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  return (
    <>
      <MetaTitleWithSeparator>
        {t("company.search.freelancers.offers.title")}
      </MetaTitleWithSeparator>

      <CompanyLayoutMainContentContainer>
        <Container size="full" class="mb-6 w-full">
          <div class="mb-8">
            <Button
              variant="ghost"
              size="sm"
              class="mb-4 p-0 h-auto hover:bg-transparent"
              onClick={() => navigate("/profile/company/search/freelancers")}
            >
              <AiOutlineArrowLeft size={20} class="mr-2" />
              {t("common.back")}
            </Button>
            <h1 class="text-3xl font-bold text-gray-900 mb-2">
              {t("company.search.freelancers.offers.title")}
            </h1>
            <p class="text-gray-600 text-base">
              {t("company.search.freelancers.offers.subtitle")}
            </p>
          </div>

          <Show
            when={jobOffers.isLoading}
            fallback={
              <Show
                when={
                  jobOffers.data?.job_offers &&
                  jobOffers.data.job_offers.length > 0
                }
                fallback={
                  <div class="text-center py-12">
                    <p class="text-gray-600">
                      {t("company.search.freelancers.offers.noOffers")}
                    </p>
                  </div>
                }
              >
                <div class="space-y-4">
                  <For each={visibleJobOffers()}>
                    {(jobOffer) => (
                      <SearchOfferCard
                        job={jobOffer}
                        isInactive={() =>
                          freelanceSearchStore.jobOfferId !== jobOffer.id
                        }
                        onClick={() => handleJobOfferClick(jobOffer.id)}
                      />
                    )}
                  </For>
                </div>
                <Show when={hasMore()}>
                  <Container
                    size={"4xl"}
                    class="mx-auto flex flex-col items-center justify-center h-24 border-t border-gray-300 mt-4 mb-4"
                  >
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleLoadMore}
                    >
                      {t("joblist.show_more_jobs")}
                    </Button>
                  </Container>
                </Show>
              </Show>
            }
          >
            <div class="text-center py-12">
              <p class="text-gray-600">
                {t("company.search.freelancers.offers.loading")}
              </p>
            </div>
          </Show>
        </Container>
      </CompanyLayoutMainContentContainer>
    </>
  );
}
